import React, { useCallback, useEffect, useRef, useState } from 'react';
import Decimal from 'decimal.js';
import { Form, Formik, useFormikContext } from 'formik';
import isEmpty from 'lodash/isEmpty';
import type { Moment } from 'moment';
import moment from 'moment';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import { DatePickerTextFieldFormik, MonetaryValueTextFieldFormik } from '~/components/TextFieldFormik';
import { DateFormat, getDateFromServerDateTime, getPeriodInDays } from '~/DateTimeUtils';
import formatDaysAsWeeksAndDays from '~/Utils/formatDaysAsWeekAndDays';

import { useFormatAmount } from '../helpers';
import TEXTS from '../texts';
import type { IncomeProtectionDaSettingType, PaymentSummaryType, PrePaymentFormValuesType } from '../types';

const addDaysToDateOnlyISOString = (date: string, days: number): string => {
  return moment(date).add(days, 'days').format(DateFormat.ISO);
};

const calculateAmountForGivenDays = (claimantWeeklyLimit: number | string, days: number): number => {
  if (days <= 0) {
    return 0;
  }

  return new Decimal(claimantWeeklyLimit).times(days).dividedBy(7).toDecimalPlaces(2).toNumber();
};

const calculateWeeklyAmount = (amount: number, days: number): number => {
  if (days <= 0 || amount <= 0) {
    return 0;
  }

  return new Decimal(amount).dividedBy(new Decimal(days).dividedBy(7)).toDecimalPlaces(2).toNumber();
};

// The paid periods don't overlap, so subtracting each one's overlap with the payment counts every day once
const countDaysNotYetPaid = (
  startDate: string,
  endDate: string,
  paidPeriods: PaymentSummaryType['paid_periods']
): number =>
  paidPeriods.reduce(
    (daysNotYetPaid, paidPeriod) => {
      const overlapStart = moment.max(moment(startDate), moment(paidPeriod.start_date));
      const overlapEnd = moment.min(moment(endDate), moment(paidPeriod.end_date));
      return overlapStart.isAfter(overlapEnd)
        ? daysNotYetPaid
        : daysNotYetPaid - getPeriodInDays(overlapStart.toDate(), overlapEnd.toDate());
    },
    getPeriodInDays(startDate, endDate)
  );

const getInitialValues = (
  paymentsSummary: PaymentSummaryType | null,
  paymentSettings: IncomeProtectionDaSettingType
): PrePaymentFormValuesType => {
  const service_start_date = paymentsSummary?.last_payment_end_date
    ? addDaysToDateOnlyISOString(paymentsSummary.last_payment_end_date, 1)
    : paymentSettings.start_date;
  const service_end_date = addDaysToDateOnlyISOString(
    service_start_date,
    paymentSettings.payment_frequency_num_of_days - 1
  );
  const amount = calculateAmountForGivenDays(
    paymentSettings.claimant_weekly_limit,
    paymentSettings.payment_frequency_num_of_days
  );
  return {
    service_start_date,
    service_end_date,
    amount,
  };
};

const validationSchema = Yup.object().shape({
  service_start_date: Yup.date().nullable().required(TEXTS.required),
  service_end_date: Yup.date()
    .nullable()
    .required(TEXTS.required)
    .min(Yup.ref('service_start_date'), TEXTS.cannotBeEarlierThanStartDate),
  amount: Yup.number().nullable().moreThan(0, TEXTS.mustBeGreaterThan0).required(TEXTS.required),
});

type Warning = { title: string; note: string; key: string };
const usePrePaymentWarnings = ({
  paymentsSummary,
  paymentSetting,
  medicalCertificateExpiryDate,
}: {
  paymentsSummary: PaymentSummaryType | null;
  paymentSetting: IncomeProtectionDaSettingType;
  medicalCertificateExpiryDate: string | null;
}): Warning[] => {
  const [warnings, setWarnings] = useState<Warning[]>([]);
  const { errors, values } = useFormikContext<PrePaymentFormValuesType>();
  const formatAmount = useFormatAmount();
  const lastPaymentEndDate = paymentsSummary?.last_payment_end_date;
  const totalDaysPaid = paymentsSummary?.total_days_paid ?? 0;
  const paidPeriods = paymentsSummary?.paid_periods;

  const hasErrors = !isEmpty(errors);
  useEffect(() => {
    const warnings: Warning[] = [];
    if (hasErrors) {
      setWarnings(warnings);
      return;
    }

    const expectedStartDate = lastPaymentEndDate
      ? addDaysToDateOnlyISOString(lastPaymentEndDate, 1)
      : paymentSetting.start_date;
    if (values.service_start_date !== expectedStartDate) {
      warnings.push({
        key: 'startDateWarning',
        title: TEXTS.startDateDoesNotMatchExpectedStartDate,
        note: TEXTS.startDateDoesNotMatchExpectedStartDateNote(
          getDateFromServerDateTime(expectedStartDate),
          getDateFromServerDateTime(values.service_start_date)
        ),
      });
    }

    const paymentPeriodInDays = getPeriodInDays(values.service_start_date, values.service_end_date);
    const expectedTotalDaysPaid =
      totalDaysPaid + countDaysNotYetPaid(values.service_start_date, values.service_end_date, paidPeriods ?? []);
    if (expectedTotalDaysPaid > paymentSetting.benefit_period_num_of_days) {
      const daysOver = expectedTotalDaysPaid - paymentSetting.benefit_period_num_of_days;
      warnings.push({
        key: 'benefitPeriodWarning',
        title: TEXTS.benefitPeriodIsLongerThenSetting,
        note: TEXTS.benefitPeriodIsLongerThenSettingNote(
          formatDaysAsWeeksAndDays(paymentSetting.benefit_period_num_of_days, formatDaysAsWeeksAndDays.AND_SEPARATOR),
          formatDaysAsWeeksAndDays(daysOver, formatDaysAsWeeksAndDays.AND_SEPARATOR)
        ),
      });
    } else if (paymentPeriodInDays > paymentSetting.payment_frequency_num_of_days) {
      warnings.push({
        key: 'paymentPeriodWarning',
        title: TEXTS.paymentPeriodIsLongerThenSetting,
        note: TEXTS.paymentPeriodIsLongerThenSettingNote(
          paymentSetting.payment_frequency_num_of_days,
          paymentPeriodInDays
        ),
      });
    }

    if (medicalCertificateExpiryDate && values.service_end_date) {
      if (moment(values.service_end_date).isAfter(moment(medicalCertificateExpiryDate))) {
        warnings.push({
          key: 'medicalCertificateExpiryDateWarning',
          title: TEXTS.medicalCertificateExpiryDateIsBeforeServiceEndDate,
          note: TEXTS.medicalCertificateExpiryDateIsBeforeServiceEndDateNote(
            getDateFromServerDateTime(medicalCertificateExpiryDate),
            getDateFromServerDateTime(values.service_end_date)
          ),
        });
      }
    }

    const weeklyAmount = calculateWeeklyAmount(values.amount, paymentPeriodInDays);
    if (weeklyAmount > Number(paymentSetting.claimant_weekly_limit)) {
      warnings.push({
        key: 'amountWarning',
        title: TEXTS.amountLargerThanClaimantWeeklyLimit,
        note: TEXTS.amountLargerThanClaimantWeeklyLimitNote(
          formatAmount(paymentSetting.claimant_weekly_limit),
          formatAmount(new Decimal(weeklyAmount).minus(new Decimal(paymentSetting.claimant_weekly_limit)).toNumber()),
          paymentPeriodInDays > 7
        ),
      });
    }
    setWarnings(warnings);
  }, [
    lastPaymentEndDate,
    totalDaysPaid,
    paidPeriods,
    paymentSetting.start_date,
    paymentSetting.payment_frequency_num_of_days,
    paymentSetting.benefit_period_num_of_days,
    paymentSetting.claimant_weekly_limit,
    medicalCertificateExpiryDate,
    hasErrors,
    values.service_start_date,
    values.service_end_date,
    values.amount,
    formatAmount,
  ]);

  return warnings;
};

const IncomeProtectionPrePaymentInner = ({
  onCancel,
  paymentSetting,
  medicalCertificateExpiryDate,
  paymentsSummary,
}: IncomeProtectionPrePaymentInnerProps) => {
  const { values, setFieldValue, isSubmitting, submitForm } = useFormikContext<PrePaymentFormValuesType>();
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const warnings = usePrePaymentWarnings({ paymentsSummary, paymentSetting, medicalCertificateExpiryDate });

  const computeAmount = useCallback(() => {
    const serviceStartDate = valuesRef.current.service_start_date;
    const serviceEndDate = valuesRef.current.service_end_date;
    if (!serviceStartDate || !serviceEndDate) {
      return;
    }
    const days = getPeriodInDays(serviceStartDate, serviceEndDate);
    const amount = calculateAmountForGivenDays(paymentSetting.claimant_weekly_limit, days);
    setFieldValue('amount', amount);
  }, [setFieldValue, paymentSetting.claimant_weekly_limit]);

  const handleDateChange = useCallback(
    async (id: string, momentDate: Moment | null) => {
      if (!momentDate) {
        setFieldValue(id, null);
      } else {
        await setFieldValue(id, momentDate.format(DateFormat.ISO));
        computeAmount();
      }
    },
    [setFieldValue, computeAmount]
  );

  const handleStartDateChange = useCallback(
    (momentDate: Moment) => {
      handleDateChange('service_start_date', momentDate);
    },
    [handleDateChange]
  );

  const handleEndDateChange = useCallback(
    (momentDate: Moment) => {
      handleDateChange('service_end_date', momentDate);
    },
    [handleDateChange]
  );

  const paymentPeriod =
    values.service_start_date && values.service_end_date
      ? getPeriodInDays(values.service_start_date, values.service_end_date)
      : 0;
  const totalDaysHelperText = paymentPeriod > 0 ? TEXTS.totalDays(paymentPeriod) : null;

  return (
    <Form className="grid grid-cols-2 gap-16">
      <DatePickerTextFieldFormik
        id="service_start_date"
        label={TEXTS.prePaymentFormLabels.service_start_date}
        clearable
        onChange={handleStartDateChange}
        className="w-full"
      />
      <DatePickerTextFieldFormik
        id="service_end_date"
        label={TEXTS.prePaymentFormLabels.service_end_date}
        clearable
        onChange={handleEndDateChange}
        className="w-full"
        helperText={totalDaysHelperText}
      />
      <MonetaryValueTextFieldFormik id="amount" label={TEXTS.prePaymentFormLabels.amount} />
      {warnings.map((warning) => (
        <AlertBanner
          className="col-span-2"
          key={warning.key}
          withIcon
          alertType={AlertBanner.ALERT_TYPES.WARNING}
          title={warning.title}
          note={warning.note}
        />
      ))}
      <div className="col-span-2 flex justify-end gap-16">
        <Button type="button" variant="outlined" color="primary" onClick={onCancel}>
          {TEXTS.cancel}
        </Button>
        <Button
          // If there are any warnings, the user will need to explicitly click on the button to proceed
          type={warnings.length > 0 ? 'button' : 'submit'}
          variant="contained"
          color="primary"
          disabled={isSubmitting}
          onClick={submitForm}
        >
          {TEXTS.submitPrePayment}
        </Button>
      </div>
    </Form>
  );
};

const IncomeProtectionPrePaymentDialog = ({
  isOpen,
  isLoading,
  paymentSetting,
  medicalCertificateExpiryDate,
  paymentsSummary,
  onSubmit,
  onCancel,
}: IncomeProtectionPrePaymentDialogProps) => {
  return (
    <CardDialog
      isDialog
      isOpen={isOpen}
      onClose={onCancel}
      title={TEXTS.prePaymentFormTitle}
      titleVariant="h6"
      fullWidth
    >
      {isLoading ? (
        <SkeletonTable rowsCount={2} columnsCount={2} />
      ) : (
        <Formik
          initialValues={getInitialValues(paymentsSummary, paymentSetting)}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          <IncomeProtectionPrePaymentInner
            onCancel={onCancel}
            paymentSetting={paymentSetting}
            paymentsSummary={paymentsSummary}
            medicalCertificateExpiryDate={medicalCertificateExpiryDate}
          />
        </Formik>
      )}
    </CardDialog>
  );
};

interface IncomeProtectionPrePaymentInnerProps {
  onCancel: () => void;
  paymentSetting: IncomeProtectionDaSettingType;
  medicalCertificateExpiryDate: string | null;
  paymentsSummary: PaymentSummaryType | null;
}

interface IncomeProtectionPrePaymentDialogProps extends IncomeProtectionPrePaymentInnerProps {
  isOpen: boolean;
  isLoading: boolean;
  onSubmit: (values: PrePaymentFormValuesType) => void;
}

export default IncomeProtectionPrePaymentDialog;
